import { useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    FinancialDocumentView,
    FinancialDocumentViewSchema,
} from "@common/types/views/financialDocumentViews";
import { FinancialDocumentsViewContainer } from "@components/documents/FinancialDocumentsView/FinancialDocumentsViewContainer";
import { FinancialDocumentsTaskTableProvider } from "@components/tasks/TaskBody/FinancialDocuments/FinancialDocumentsTaskTableProvider";
import { useFinancialDocumentsTaskRows } from "@components/tasks/TaskBody/FinancialDocuments/useFinancialDocumentsTaskRows";
import {
    TemplateTaskDataTable,
    TemplateTaskTableProps,
} from "./TemplateTaskDataTable";
import { ResolvedTaskView } from "./useResolvedTaskView";

interface Props {
    templateView: FinancialDocumentView;
    onTemplateViewChange: (view: FinancialDocumentView) => void;
    resolvedView: ResolvedTaskView;
    workspaceId: string | null;
}

const ResolvedFinancialDocumentsTable = ({
    view,
    onViewChange,
    emptyView,
}: TemplateTaskTableProps<FinancialDocumentView>) => {
    const {
        queryFilters,
        rows,
        total,
        totalPages,
        page,
        setPage,
        sort,
        handleSortChange,
    } = useFinancialDocumentsTaskRows({ viewFilters: view.filters });
    const pagination = useMemo(
        () => ({ currentPage: page, totalPages, onPageChange: setPage }),
        [page, totalPages, setPage],
    );

    return (
        <FinancialDocumentsTaskTableProvider
            view={view}
            queryFilters={queryFilters}
            total={total}
        >
            <FinancialDocumentsViewContainer
                view={view}
                documents={rows}
                total={total}
                pagination={pagination}
                onDocumentClick={noop}
                sort={sort}
                onSortChange={handleSortChange}
                emptyView={emptyView}
                onViewChange={onViewChange}
            />
        </FinancialDocumentsTaskTableProvider>
    );
};

const renderFinancialDocumentsTable = (
    props: TemplateTaskTableProps<FinancialDocumentView>,
) => <ResolvedFinancialDocumentsTable {...props} />;

/**
 * The financial documents a template task would show for the previewed client.
 * Read-only: no selection, bulk actions or row details.
 */
export const TemplateFinancialDocumentsDataTable = (props: Props) => (
    <TemplateTaskDataTable
        {...props}
        viewSchema={FinancialDocumentViewSchema}
        rowsLabel="documents"
        renderTable={renderFinancialDocumentsTable}
    />
);
